import { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, ExternalLink } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, stamp } from "./ui";
import IndexPushButton from "./IndexPush";
import { ClaudeModelSelect } from "./ClaudeModelSelect";
import { ContentJobStatus } from "./ContentJobStatus";

// Local copy + 3 FAQ for every city × occasion page (e.g. "birthday flowers Marrakech").
export default function OccasionPagesPage() {
  const [data, setData] = useState(null);
  const [cityId, setCityId] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [model, setModel] = useState("");

  const load = useCallback(() => api.get(`/admin/combos/local-copy${cityId ? `?city_id=${cityId}` : ""}`)
    .then((r) => { setData(r.data); if (!cityId && r.data.city_id) setCityId(r.data.city_id); })
    .catch((e) => setMsg(errText(e))), [cityId]);
  useEffect(() => { load(); }, [load]);

  const running = data?.job && ["queued", "running"].includes(data.job.status);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [running, load]);

  const post = async (url, key, note) => {
    if (["all", "city"].includes(key) && !window.confirm("Generate these occasion pages using the selected Claude model? Usage is billed to your Anthropic account.")) return;
    setBusy(key); setMsg("");
    try { await api.post(url, { model: model || null }); setMsg(note); load(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-occasion-pages">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl text-ink mb-1">Occasion pages · local SEO</h1>
          <p className="text-sm text-muted max-w-2xl">
            Each city × occasion page (e.g. <i>birthday flowers Marrakech</i>) gets its own short local paragraph and three
            page-specific FAQ in EN/FR/ES/AR, written by Claude. The FAQ also feed the FAQ rich result and the crawler snapshots.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button className="btn-primary text-xs px-4 inline-flex items-center gap-1.5" disabled={!!busy || running}
                  onClick={() => post("/admin/combos/local-copy/all", "all", "Writing every occasion page in the background — this takes several hours.")}
                  data-testid="occasion-pages-generate-all">
            {busy === "all" || running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Write all {data?.total || ""} pages
          </button>
          <IndexPushButton testid="occasion-index-push" />
        </div>
      </div>

      {msg ? <p className="text-sm text-brass mb-3" data-testid="occasion-pages-message">{msg}</p> : null}
      <div className="mb-4"><ClaudeModelSelect value={model} onChange={setModel} testid="occasion-claude-model" disabled={!!busy || running} /></div>
      <ContentJobStatus job={data?.job} model={model} onChanged={load} testid="occasion-pages-job" unit="pages" />
      <p className="text-xs text-muted mb-4" data-testid="occasion-pages-coverage">
        {data?.written ?? 0} of {data?.total ?? 0} combo pages written.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select className="field max-w-xs text-xs" value={cityId} data-testid="occasion-city-select"
                onChange={(e) => { setCityId(e.target.value); setData(null); }}>
          {(data?.cities || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn-ghost text-xs px-3 inline-flex items-center gap-1.5" disabled={!!busy || running || !cityId}
                onClick={() => post(`/admin/combos/local-copy/city/${cityId}`, "city", `Writing every occasion page for ${data?.city_name}…`)}
                data-testid="occasion-generate-city">
          {busy === "city" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Write this city
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-start smallcaps border-b border-brass-light">
            <th className="p-3 text-start">Occasion</th><th className="p-3 text-start">Local paragraph (EN)</th>
            <th className="p-3">FAQ</th><th className="p-3 text-start">Generated</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.occasion_id} className="border-b border-brass-light/60" data-testid={`combo-row-${r.occasion_id}`}>
                <td className="p-3 whitespace-nowrap">
                  <a href={r.path} target="_blank" rel="noreferrer" className="underline decoration-brass underline-offset-2 inline-flex items-center gap-1">
                    {r.name} <ExternalLink size={11} />
                  </a>
                </td>
                <td className="p-3 text-muted">{r.excerpt ? `${r.excerpt}…` : <span className="text-warning">not written yet</span>}</td>
                <td className="p-3 text-center tabular">{r.faq_count || 0}</td>
                <td className="p-3 text-muted whitespace-nowrap">{r.generated_at ? stamp(r.generated_at) : "—"}</td>
                <td className="p-3 text-end">
                  <button className="text-[11px] border border-brass px-2 py-1 rounded-sm text-brass hover:bg-brass hover:text-plaster transition-colors inline-flex items-center gap-1"
                          disabled={!!busy || running}
                          onClick={() => post(`/admin/combos/${cityId}/${r.occasion_id}/local-copy`, r.occasion_id, `${r.name}: Claude is writing (about a minute)…`)}
                          data-testid={`combo-generate-${r.occasion_id}`}>
                    {busy === r.occasion_id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {r.generated_at ? "Rewrite" : "Write"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
